import React, { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../../context/LoadingContext";
import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import AlertModal from "../../Requirement/components/common/AlerModel";
import * as XLSX from "xlsx";
import {
  fetchData,
  postQuery,
} from "../../../services/BackendServices";
import "../../../styles/general.css";
import "../../../styles/amtpl.css";
import { v4 as uuidv4 } from "uuid";
import SellerForm from "./SellerForm";
import config from "../../../aws-exports";

const Seller = () => {
  const { setLoading, loading } = useLoading();
  const [amtplSellers, setAMTPLSellers] = useState([]);
  const [selectedAMTPLSeller, setSelectedAMTPLSeller] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [currentAMTPLSellerPage, setCurrentAMTPLSellerPage] = useState(1);
  const [totalAMTPLSellerPages, setTotalAMTPLSellerPages] = useState(1);
  const [names, setNames] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [zipcodes, setZipcodes] = useState([]);
  const [sellerTypes] = useState([
    "Farmer Producer Company",
    "Trader/ Stockist",
    "SellerFarmer",
    "Large Farmer",
  ]);
  const [entitiesFilterTerms, setEntitiesFilterTerms] = useState({
    email: "",
    company: "",
    location: "",
    zipcode: "",
    status: "",
  });
  const [file, setFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const pageSize = 5;

  const bucket = process.env.REACT_APP_AMTPL_BUCKET;
  let bucketfolder = "public/media/amtpl";
  const filePath = `${bucketfolder}/`;
  const fileUrl = `https://${bucket}.s3.${config.aws_project_region}.amazonaws.com/${filePath}`;

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const statusOptions = [
    { value: "", label: "ALL" },
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" },
  ];

  const fetchFilterOptions = useCallback(async () => {
    setLoading(true);
    try {
      const body = {
        target: "listAMTPLUsersAdm",
        option: "seller",
        filterTerms: {},
        rowStart: 0,
        pageSize: 10000,
      };
      const result = await fetchData(body);
      if (result && (result.items || result.statusCode === 200)) {
        const buyers = result.items || [];
        if (buyers.length > 0) {
          const uniqueNames = [
            ...new Set(
              buyers
                .map(
                  (p) =>
                    `${p.FirstName || ""} ${p.LastName || ""} <${
                      p.EmailID || ""
                    }>`
                )
                .filter((name) => name.trim() !== "<>")
            ),
          ];
          const uniqueCompanies = [
            ...new Set(buyers.map((p) => p.CompanyName).filter(Boolean)),
          ];
          const uniqueLocations = [
            ...new Set(buyers.map((p) => p.State).filter(Boolean)),
          ];
          const uniqueZipcodes = [
            ...new Set(buyers.map((p) => p.ZipCode).filter(Boolean)),
          ];
          setNames(uniqueNames);
          setCompanies(uniqueCompanies);
          setLocations(uniqueLocations);
          setZipcodes(uniqueZipcodes);
        } else {
          setNames([]);
          setCompanies([]);
          setLocations([]);
          setZipcodes([]);
        }
      } else {
        console.error("Error fetching filter options: ", result);
        setNames([]);
        setCompanies([]);
        setLocations([]);
        setZipcodes([]);
      }
    } catch (error) {
      console.error("Error fetching filter options: ", error);
      setNames([]);
      setCompanies([]);
      setLocations([]);
      setZipcodes([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const fields = [
    {
      name: "email",
      label: "Name",
      type: "dropdown",
      options: [
        { value: "", label: "Select Name" },
        ...names.map((name) => {
          const match = name.match(/<([^>]+)>/);
          const keyAndValue = match ? match[1].trim() : "";
          return {
            value: keyAndValue,
            label: name,
          };
        }),
      ],
      value: entitiesFilterTerms.email,
      onChange: (value) => {
        setEntitiesFilterTerms((prev) => ({ ...prev, email: value }));
      },
    },
    {
      name: "company",
      label: "Company",
      type: "dropdown",
      options: [
        { value: "", label: "Select Company" },
        ...companies.map((company) => ({
          value: company,
          label: company,
        })),
      ],
      value: entitiesFilterTerms.company,
      onChange: (value) => {
        setEntitiesFilterTerms((prev) => ({ ...prev, company: value }));
      },
    },
    {
      name: "location",
      label: "Location",
      type: "dropdown",
      options: [
        { value: "", label: "Select Location" },
        ...locations.map((location) => ({
          value: location,
          label: location,
        })),
      ],
      value: entitiesFilterTerms.location,
      onChange: (value) => {
        setEntitiesFilterTerms((prev) => ({ ...prev, location: value }));
      },
    },
    {
      name: "zipcode",
      label: "Zipcode",
      type: "dropdown",
      options: [
        { value: "", label: "Select Zipcode" },
        ...zipcodes.map((zipcode) => ({
          value: zipcode,
          label: zipcode,
        })),
      ],
      value: entitiesFilterTerms.zipcode,
      onChange: (value) => {
        setEntitiesFilterTerms((prev) => ({ ...prev, zipcode: value }));
      },
    },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: statusOptions,
      value: entitiesFilterTerms.status,
      onChange: (value) => {
        setEntitiesFilterTerms((prev) => ({ ...prev, status: value }));
      },
    },
  ];

  const columns = [
    {
      id: "AMTPLUserID",
      numeric: false,
      label: "Supplier Id",
      renderCell: (row) => row.AMTPLUserID || "N/A",
    },
    {
      id: "SupplierName",
      numeric: false,
      label: "Supplier Name",
      renderCell: (row) =>
        `${row.FirstName || ""} ${row.LastName || ""}`.trim() || "N/A",
    },
    {
      id: "CompanyName",
      numeric: false,
      label: "Company",
      renderCell: (row) => row.CompanyName || "N/A",
    },
    {
      id: "Country",
      numeric: false,
      label: "Country",
      renderCell: (row) => row.Country || "N/A",
    },
    {
      id: "State",
      numeric: false,
      label: "State",
      renderCell: (row) => row.State || "N/A",
    },
    {
      id: "Phone1",
      numeric: false,
      label: "Phone",
      renderCell: (row) => row.Phone1 || "N/A",
    },
    {
      id: "EmailID",
      numeric: false,
      label: "Email",
      renderCell: (row) => row.EmailID || "N/A",
    },
    {
      id: "Status",
      numeric: false,
      label: "Status",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";
        switch (row.Status) {
          case "INACTIVE":
            bgColor = "#f85d4f25";
            textColor = "#e2461f";
            break;
          case "ACTIVE":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#f0f0f0";
            textColor = "#000";
        }
        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "10px",
              fontWeight: 500,
            }}
          >
            {row.Status || "N/A"}
          </span>
        );
      },
    },
  ];

  const fetchAMTPLUsers = useCallback(
    async (customParams = {}) => {
      setLoading(true);
      try {
        const updatedEntitiesFilterTerms = {
          ...entitiesFilterTerms,
          locationPartial: entitiesFilterTerms.location,
        };
        delete updatedEntitiesFilterTerms.location;
        const filterTerms = {
          ...updatedEntitiesFilterTerms,
          ...customParams.filterTerms,
        };
        Object.keys(filterTerms).forEach((key) => {
          if (!filterTerms[key]) delete filterTerms[key];
        });
        const body = {
          target: "listAMTPLUsersAdm",
          option: "seller",
          filterTerms: filterTerms,
          rowStart:
            customParams.rowStart || (currentAMTPLSellerPage - 1) * pageSize,
          pageSize: pageSize,
          ...customParams,
        };
        const result = await fetchData(body);
        if (result && (result.items || result.statusCode === 200)) {
          const sellers = result.items || [];
          setAMTPLSellers(sellers);
          setTotalAMTPLSellerPages(
            Math.ceil((result.total || sellers.length) / pageSize)
          );
        } else {
          console.error("Error fetching Sellers: ", result);
          setAMTPLSellers([]);
          setTotalAMTPLSellerPages(1);
        }
      } catch (error) {
        console.error("Error fetching Sellers: ", error);
        setAMTPLSellers([]);
        setTotalAMTPLSellerPages(1);
      } finally {
        setLoading(false);
      }
    },
    [currentAMTPLSellerPage, entitiesFilterTerms, setLoading]
  );

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
    setUploadError(null);
  };

  const requiredFieldsExcel = [
    "AMTPLUserID",
    "EmailID",
    "GPEmailID",
    "FirstName",
    "Phone1",
    "Address1",
  ];

  const validateExcelData = (data) => {
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      for (const field of requiredFieldsExcel) {
        if (!row[field] || row[field].toString().trim() === "") {
          return `Missing required field "${field}" in row ${i + 2}`;
        }
      }
      if (row.EmailID && !/\S+@\S+\.\S+/.test(row.EmailID)) {
        return `Invalid email format for EmailID in row ${i + 2}`;
      }
      // if (row.GPEmailID && !/\S+@\S+\.\S+/.test(row.GPEmailID)) {
      //   return `Invalid email format for GPEmailID in row ${i + 2}`;
      // }
      if (row.Age && !Number.isInteger(Number(row.Age))) {
        return `Invalid Age (must be an integer) in row ${i + 2}`;
      }
    }
    return null;
  };

  const handleUpload = async () => {
    if (!file) {
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: "Please select an excel file for uploading.",
        autoClose: false,
      });
      return;
    }
    setLoading(true);
    setUploadError(null);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const parsedData = XLSX.utils.sheet_to_json(sheet);
          if (parsedData.length === 0) {
            setUploadError("The uploaded file is empty.");
            setLoading(false);
            return;
          }
          const body = {
            target: "importAMTPLUsers",
            option: "seller",
            usersData: parsedData,
          };
          const result = await postQuery(body, "backendops");
          if (result) {
            setModal({
              show: true,
              type: "success",
              title: "Success!",
              message: `Excel data imported successfully.`,
              autoClose: false,
              nextAction: () => {
                setFile(null);
                document.getElementById("fileInput").value = "";
              },
            });
            await fetchAMTPLUsers();
            await fetchFilterOptions();
          } else {
            setUploadError("Failed to import data. Please try again.");
            console.error("Error importing Excel data: ", result);
          }
        } catch (error) {
          try {
            const errors = error.errors;
            const failedEmails = errors.map((err) => err.email).join(", ");
            const errorText = `The following records could not be processed because the 'Group Name' field is missing: ${failedEmails}. Please ensure the 'Group Name' column is filled in your Excel file.`;
            setUploadError(errorText);
          } catch (err) {
            console.error("Error processing error response:", err);
            setUploadError(
              "An unexpected error occurred while processing the file. Please try again or contact support."
            );
          }
        } finally {
          setLoading(false);
        }
      };
      reader.onerror = () => {
        console.error("FileReader error occurred");
        setUploadError("Error reading the file. Please try again.");
        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error initiating file upload:", error);
      setUploadError("Error uploading file. Please try again.");
      setLoading(false);
    }
  };

  const handleSearchFilter = (searchValues) => {
    setEntitiesFilterTerms(searchValues);
    setCurrentAMTPLSellerPage(1);
    fetchAMTPLUsers({
      filterTerms: searchValues,
      rowStart: 0,
    });
  };

  const handlePageChange = useCallback((page) => {
    setCurrentAMTPLSellerPage(page);
  }, []);

  const handleRowClick = (seller) => {
    setSelectedAMTPLSeller(seller);
    setFormMode("edit");
    setShowForm(true);
  };

  const handleAddClick = () => {
    setSelectedAMTPLSeller(null);
    setFormMode("add");
    setShowForm(true);
  };

  const handleFormSave = async (formData) => {
    setLoading(true);
    try {
      let body = null;
      if (formMode === "add") {
        body = {
          target: "addusersmanual",
          userData: {
            ...((formData) => {
              const { errors, ...rest } = formData;
              return rest;
            })(formData),
            GPEmailID: formData.GPEmailID || " ",
            password: "zXCVBNM123456789!",
            UserType: "seller",
            AMTPLUserID: uuidv4(),
          },
        };
      } else {
        body = {
          target: "updateAMTPLUser",
          updateAMTPLUserInput: {
            AMTPLUserID: formData.AMTPLUserID,
            EmailID: formData.EmailID,
            GPEmailID: formData.GPEmailID || " ",
            FirstName: formData.FirstName || "",
            LastName: formData.LastName || "",
            Country: formData.Country || "",
            State: formData.State || "",
            City: formData.City || "",
            Phone1: formData.Phone1 || "",
            Phone2: formData.Phone2 || "",
            Address1: formData.Address1 || "",
            Address2: formData.Address2 || "",
            ZipCode: formData.ZipCode || "",
            CompanyName: formData.CompanyName || "",
            CompanyZipCode: formData.CompanyZipCode || "",
            CompanyPhone1: formData.CompanyPhone1 || "",
            CompanyPhone2: formData.CompanyPhone2 || "",
            CompanyPhone3: formData.CompanyPhone3 || "",
            CompanyPhone4: formData.CompanyPhone4 || "",
            CompanyAddress1: formData.CompanyAddress1 || "",
            CompanyAddress2: formData.CompanyAddress2 || "",
            CompanyAddress3: formData.CompanyAddress3 || "",
            CompanyCity: formData.CompanyCity || "",
            CompanyCountry: formData.CompanyCountry || "",
            CompanyEmail: formData.CompanyEmail || "",
            CompanyState: formData.CompanyState || "",
            Age: formData.Age || 0,
            Gender: formData.Gender || "",
            CompanyLinkedIn: formData.CompanyLinkedIn || "",
            CompanyTwitter: formData.CompanyTwitter || "",
            CompanyWebsite: formData.CompanyWebsite || "",
            LinkedIn: formData.LinkedIn || "",
            Twitter: formData.Twitter || "",
            Website: formData.Website || "",
            Status: formData.Status || "ACTIVE",
          },
        };
      }
      // const result = await postQuery(body, "backendops");
      const result = await postQuery(
        body,
        formMode === "add" ? "backendops" : "dataops"
      );
      if (result) {
        setModal({
          show: true,
          type: "success",
          title: "Success!",
          message: `Seller ${
            formMode === "add" ? "added" : "updated"
          } successfully.`,
          autoClose: false,
          nextAction: () => {
            setShowForm(false);
            setSelectedAMTPLSeller(null);
          },
        });
        await fetchAMTPLUsers();
        await fetchFilterOptions();
      } else {
        setModal({
          show: true,
          type: "error",
          title: "Error!",
          message: `Error ${
            formMode === "add" ? "adding" : "updating"
          } seller.`,
          autoClose: false,
        });
      }
    } catch (error) {
      console.error(
        `Error ${formMode === "add" ? "adding" : "updating"} seller: `,
        error
      );
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message:
          error.message ||
          `Error ${formMode === "add" ? "adding" : "updating"} seller.`,
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedAMTPLSeller(null);
  };

  const handleSellerResetPassword = async () => {
    if (!selectedAMTPLSeller) return;
    setLoading(true);
    try {
      const body = {
        target: "resetPassword",
        option: "seller",
        userId: selectedAMTPLSeller.AMTPLUserID,
        email: selectedAMTPLSeller.EmailID,
      };
      const result = await postQuery(body);
      if (result && result.statusCode === 200) {
        setModal({
          show: true,
          type: "success",
          title: "Success!",
          message: "Password reset successfully.",
          autoClose: false,
        });
      } else {
        console.error("Error resetting password: ", result);
        setModal({
          show: true,
          type: "error",
          title: "Error!",
          message: "Error resetting password.",
          autoClose: false,
        });
      }
    } catch (error) {
      console.error("Error resetting password: ", error);
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: error.message || "Error resetting password.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchAMTPLUsers();
  }, [fetchAMTPLUsers, currentAMTPLSellerPage, entitiesFilterTerms]);

  return (
    <div className="buyer-dashboard">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card p-4">
              <h4 className="text-center mb-3">Import Data</h4>
              <div className="alert alert-info mb-4 text-center" role="alert">
                To ensure a smooth import, please download our sample format
                first. This will help you structure your data correctly.
              </div>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="text-center">
                  <a
                    href={`${fileUrl}sampleformat/SampleFormatSeller.xlsx`}
                    className="btn btn-primary"
                  >
                    Download Sample Format
                  </a>
                  <small className="text-muted d-block mt-1">
                    (.xlsx format)
                  </small>
                </div>
                <div className="text-center">
                  <label
                    htmlFor="fileInput"
                    className="btn btn-outline-secondary"
                    style={{ border: "dashed 1px #ccc", minWidth: "180px" }}
                  >
                    Upload Your File
                  </label>
                  <input
                    type="file"
                    id="fileInput"
                    onChange={handleFileChange}
                    accept=".csv,.xlsx,.xls"
                    className="d-none"
                  />
                  {file?.name}
                  {uploadError && (
                    <div className="text-danger mt-2 small">{uploadError}</div>
                  )}
                </div>
                <div className="text-center">
                  <button
                    onClick={handleUpload}
                    className="submit-btn-more"
                    disabled={!file}
                    style={{ minWidth: "180px" }}
                  >
                    Import
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="search-filter4">
          <div className="title4">
            <div className="head-text">Seller</div>
          </div>
          <div className="filter4">
            <SearchFilter fields={fields} onSearch={handleSearchFilter} />
          </div>
          <div
            className="head-btn"
            style={{ display: "flex", gap: "10px", alignItems: "center" }}
          >
            <button className="submit-btn-more" onClick={handleAddClick}>
              Add
            </button>
          </div>
        </div>
        {!loading && amtplSellers.length > 0 && (
          <DataTable
            columns={columns}
            data={amtplSellers}
            total={totalAMTPLSellerPages * pageSize}
            title="Seller Information"
            rowsPerPage={pageSize}
            onPageChange={handlePageChange}
            onRowClick={handleRowClick}
            currentPage={currentAMTPLSellerPage}
          />
        )}
        {!loading && amtplSellers.length === 0 && (
          <div className="text-center p-4">
            <p>No matching users available</p>
          </div>
        )}
        {showForm && (
          <>
            <SellerForm
              seller={selectedAMTPLSeller}
              onSave={handleFormSave}
              onCancel={handleFormCancel}
              mode={formMode}
              handleSellerResetPassword={handleSellerResetPassword}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Seller;
